// React hooks for analytics with comprehensive logging
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  track,
  trackPageView,
  trackScreenView,
  logger,
} from './analytics-logger';
import { safeSearch, safeHash, safeState } from './claimHandoff.js';

// Enhanced page tracking hook with deduplication
export function usePageTracking(options = {}) {
  const location = useLocation();
  const previousPathRef = useRef();
  const {
    enabled = true,
    additionalProps = {},
    trackOnMount = true,
    debounceMs = 100,
    includeSearch = false,
  } = options;

  useEffect(() => {
    if (!enabled) return;

    // Use pathname only by default to avoid leaking query params into analytics
    const currentPath = location.pathname;

    // Skip if same path (handles React strict mode double renders)
    if (previousPathRef.current === currentPath && !trackOnMount) {
      logger.log('info', `Skipping page tracking - same path: ${currentPath}`, {
        currentPath,
        previousPath: previousPathRef.current,
      });
      return;
    }

    // Debounce rapid navigation changes
    const timeoutId = setTimeout(() => {
      logger.log('info', `Page tracking triggered for: ${currentPath}`, {
        currentPath,
        previousPath: previousPathRef.current,
        // Never the raw query: a /welcome link carries a claim token (TAS-373).
        search: safeSearch(location.search),
        // Never the raw fragment: a magic-link return carries live access and
        // refresh tokens. sanitizeUrl() only guards `url` and `referrer`.
        hash: safeHash(location.hash),
        // A sign-in redirect stashes the location it came from in state.from,
        // fragment and all, which outlives the redirect.
        state: safeState(location.state),
      });

      const pageViewProps = {
        ...additionalProps,
        hash: safeHash(location.hash),
        previousPath: previousPathRef.current,
      };

      // Optionally include search params if explicitly requested
      if (includeSearch && location.search) {
        pageViewProps.search = safeSearch(location.search);
      }

      trackPageView(currentPath, pageViewProps);

      previousPathRef.current = currentPath;
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [location, enabled, additionalProps, trackOnMount, debounceMs]);

  return { currentPath: location.pathname + location.search };
}

// Screen view tracking hook for specific components
export function useScreenTracking(screenName, props = {}, dependencies = []) {
  const hasTrackedRef = useRef(false);
  const screenNameRef = useRef(screenName);

  useEffect(() => {
    // Update screen name ref if it changes
    screenNameRef.current = screenName;
  }, [screenName]);

  useEffect(() => {
    if (!screenName) {
      logger.log('warn', 'useScreenTracking called without screenName');
      return;
    }

    // Only track once per mount unless dependencies change
    if (hasTrackedRef.current && dependencies.length === 0) {
      logger.log(
        'info',
        `Skipping screen tracking - already tracked: ${screenName}`
      );
      return;
    }

    logger.log('info', `Screen tracking triggered for: ${screenName}`, {
      screenName,
      props,
      dependencies,
    });

    trackScreenView(screenName, props);
    hasTrackedRef.current = true;

    // Reset tracking flag when dependencies change
    return () => {
      if (dependencies.length > 0) {
        hasTrackedRef.current = false;
      }
    };
  }, [screenName, ...dependencies]);

  // Return function to manually trigger screen tracking
  const trackScreen = useCallback(
    (overrideProps = {}) => {
      const finalProps = { ...props, ...overrideProps };
      logger.log(
        'info',
        `Manual screen tracking for: ${screenNameRef.current}`,
        finalProps
      );
      trackScreenView(screenNameRef.current, finalProps);
    },
    [props]
  );

  return { trackScreen };
}

// Event tracking hook with automatic cleanup and deduplication
export function useEventTracking() {
  const mountTimeRef = useRef(Date.now());
  const trackedEventsRef = useRef(new Set());

  const trackEvent = useCallback((event, props = {}, options = {}) => {
    const { once = false, debounceMs = 0, requireProps = [] } = options;

    // Check required props
    const missingProps = requireProps.filter((prop) => !(prop in props));
    if (missingProps.length > 0) {
      logger.log(
        'warn',
        `Event tracking skipped - missing required props: ${event}`,
        {
          event,
          missingProps,
          providedProps: Object.keys(props),
        }
      );
      return;
    }

    // Check if event should only be tracked once
    if (once && trackedEventsRef.current.has(event)) {
      logger.log(
        'info',
        `Event tracking skipped - already tracked once: ${event}`,
        {
          event,
          props,
        }
      );
      return;
    }

    const trackingFunction = () => {
      logger.log('info', `Event tracking triggered: ${event}`, {
        event,
        props,
        mountTime: mountTimeRef.current,
        sessionAge: Date.now() - mountTimeRef.current,
      });

      track(event, {
        ...props,
        // TAS-323: this is the wall-clock moment the component mounted, NOT a
        // duration. It used to ship as `componentMountTime`, which reads like a
        // performance metric — so every average over it silently returned an
        // epoch (median 1787426326485 for session 4679). Ship it as an ISO
        // string so it can't be aggregated by mistake; `sessionAge` below is
        // the actual milliseconds-since-mount.
        componentMountedAt: new Date(mountTimeRef.current).toISOString(),
        sessionAge: Date.now() - mountTimeRef.current,
      });

      if (once) {
        trackedEventsRef.current.add(event);
      }
    };

    if (debounceMs > 0) {
      setTimeout(trackingFunction, debounceMs);
    } else {
      trackingFunction();
    }
  }, []);

  // Clear tracked events on unmount
  useEffect(() => {
    return () => {
      trackedEventsRef.current.clear();
    };
  }, []);

  return { trackEvent };
}

// Game-specific tracking hook
export function useGameTracking(sessionCode, username) {
  const { trackEvent } = useEventTracking();
  const sessionRef = useRef({ sessionCode, username });

  // Update session ref when props change
  useEffect(() => {
    sessionRef.current = { sessionCode, username };
  }, [sessionCode, username]);

  const trackGameEvent = useCallback(
    (event, additionalProps = {}) => {
      const gameProps = {
        sessionCode: sessionRef.current.sessionCode,
        username: sessionRef.current.username,
        ...additionalProps,
      };

      trackEvent(event, gameProps, { requireProps: ['sessionCode'] });
    },
    [trackEvent]
  );

  const trackGameScreen = useCallback((screenName, additionalProps = {}) => {
    const gameProps = {
      sessionCode: sessionRef.current.sessionCode,
      username: sessionRef.current.username,
      ...additionalProps,
    };

    trackScreenView(screenName, gameProps);
  }, []);

  const trackGameAction = useCallback(
    (action, additionalProps = {}) => {
      const gameProps = {
        sessionCode: sessionRef.current.sessionCode,
        username: sessionRef.current.username,
        action,
        ...additionalProps,
      };

      trackEvent('Game Action', gameProps, {
        requireProps: ['sessionCode', 'action'],
      });
    },
    [trackEvent]
  );

  return {
    trackGameEvent,
    trackGameScreen,
    trackGameAction,
  };
}

// Performance tracking hook
export function usePerformanceTracking(componentName) {
  const mountTimeRef = useRef();
  const renderCountRef = useRef(0);

  useEffect(() => {
    mountTimeRef.current = performance.now();
    renderCountRef.current += 1;

    logger.log('info', `Component performance tracking: ${componentName}`, {
      componentName,
      mountTime: mountTimeRef.current,
      renderCount: renderCountRef.current,
    });

    return () => {
      const unmountTime = performance.now();
      const totalTime = unmountTime - mountTimeRef.current;

      track('Component Performance', {
        componentName,
        totalMountTime: totalTime,
        renderCount: renderCountRef.current,
        avgRenderTime: totalTime / renderCountRef.current,
      });

      logger.log('info', `Component unmounted: ${componentName}`, {
        componentName,
        totalTime,
        renderCount: renderCountRef.current,
      });
    };
  }, [componentName]);

  const trackRender = useCallback(() => {
    renderCountRef.current += 1;
    logger.log('info', `Component render: ${componentName}`, {
      componentName,
      renderCount: renderCountRef.current,
    });
  }, [componentName]);

  return { trackRender };
}

// Error tracking hook
export function useErrorTracking(componentName) {
  const trackError = useCallback(
    (error, context = {}) => {
      logger.log('error', `Component error: ${componentName}`, {
        componentName,
        error: error.message,
        stack: error.stack,
        context,
      });

      track('Component Error', {
        componentName,
        errorMessage: error.message,
        errorStack: error.stack,
        ...context,
      });
    },
    [componentName]
  );

  const trackWarning = useCallback(
    (warning, context = {}) => {
      logger.log('warn', `Component warning: ${componentName}`, {
        componentName,
        warning,
        context,
      });

      track('Component Warning', {
        componentName,
        warning,
        ...context,
      });
    },
    [componentName]
  );

  return { trackError, trackWarning };
}

// Navigation tracking hook
export function useNavigationTracking() {
  const previousLocationRef = useRef();
  const location = useLocation();

  useEffect(() => {
    if (previousLocationRef.current) {
      const navigationData = {
        from: previousLocationRef.current.pathname,
        to: location.pathname,
        method: location.state?.navigationMethod || 'unknown',
        timestamp: Date.now(),
      };

      logger.log('info', 'Navigation tracked', navigationData);
      track('Navigation', navigationData);
    }

    previousLocationRef.current = location;
  }, [location]);

  return {
    currentLocation: location,
    previousLocation: previousLocationRef.current,
  };
}
